/**
 * Uniform ENI-grid geometry shared by `cardLayout.ts` (which computes each
 * re-gridded subnet's layout and stores it on the subnet's `data.eniGrid`)
 * and `eniDisclosure.ts` (which reads it back to place the "+N ENIs"/"Show
 * fewer" card in a real free slot). Split into its own module so the two
 * don't import each other (`cardLayout` already imports the per-subnet ENI
 * cap from `eniDisclosure`).
 */
export interface EniGridMeta {
	originX: number;
	originY: number;
	columns: number;
	cardWidth: number;
	cardHeight: number;
	gapX: number;
	gapY: number;
}

/** Top-left of slot `index` in a subnet's uniform ENI grid. */
export function eniGridSlot(meta: EniGridMeta, index: number): { x: number; y: number } {
	const col = index % meta.columns;
	const row = Math.floor(index / meta.columns);
	return {
		x: meta.originX + col * (meta.cardWidth + meta.gapX),
		y: meta.originY + row * (meta.cardHeight + meta.gapY),
	};
}

/** Runtime shape-check for a subnet's `data.eniGrid` before trusting it for slot math. */
export function isEniGridMeta(value: unknown): value is EniGridMeta {
	if (typeof value !== "object" || value === null) return false;
	const meta = value as Record<string, unknown>;
	return ["originX", "originY", "columns", "cardWidth", "cardHeight", "gapX", "gapY"].every(
		(key) => typeof meta[key] === "number"
	);
}
